// src/App.js
import React, { useState, useEffect, useMemo, createContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebaseConfig';

import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { lightTheme, darkTheme } from './Theme.js';
import MainLayout from './components/MainLayout';

// Importa componenti comuni
import Auth from './components/Auth';
import CreateTeam from './components/CreateTeam';
import Garage from './components/Garage';
import VehicleDashboard from './components/VehicleDashboard';
import SetupDetail from './components/SetupDetail';
import SetupForm from './components/SetupForm';
import SetupTemplateManager from './components/SetupTemplateManager';
import ComparePage from './components/ComparePage';
import TeamManagement from './components/TeamManagement';
import AcceptInvite from './components/AcceptInvite';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import JoinTeam from './components/JoinTeam';
import TrackManagement from './components/TrackManagement';
import Inventory from './components/Inventory';
import ChecklistManager from './components/ChecklistManager';
import EventsCalendar from './components/EventsCalendar';
import EventDetail from './components/EventDetail';
import DashboardHome from './components/DashboardHome';
import CategoryManagement from './components/CategoryManagement';
import SupplierManagement from './components/SupplierManagement';
import OrderManagement from './components/OrderManagement';
import OrderDetail from './components/OrderDetail';
import DriverManagement from './components/DriverManagement';
import WorkshopDashboard from './components/WorkshopDashboard';
import WorkOrderForm from './components/WorkOrderForm';
import WorkOrderDetail from './components/WorkOrderDetail';
import BillingDashboard from './components/BillingDashboard';
import BillingForm from './components/BillingForm';
import BillingDetail from './components/BillingDetail';
import DriverDashboard from './components/DriverDashboard';
import RaceEngineerPage from './components/race_engineer/RaceEngineerPage';
import EulaModal from './components/EulaModal';
import TireManagement from './components/tires/TireManagement';

export const TeamPlanContext = createContext({ isTrial: false });

function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [teamLogos, setTeamLogos] = useState({ light: null, dark: null });

  // --- Logica Tema (invariata) ---
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'dark';
  });
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);
  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };
  const theme = useMemo(() =>
    (mode === 'light' ? lightTheme : darkTheme),
    [mode]);
  // --- FINE Logica Tema ---

  // useEffect (autenticazione, invariato)
  useEffect(() => {
    let unsubscribeUserDoc = null;
    let unsubscribeTeamDoc = null;
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setIsLoading(true); // FIX: Blocca le rotte finché non abbiamo caricato il team!
        setUser(currentUser);
        if (unsubscribeUserDoc) unsubscribeUserDoc();
        if (unsubscribeTeamDoc) unsubscribeTeamDoc();
        const userDocRef = doc(db, 'users', currentUser.uid);
        unsubscribeUserDoc = onSnapshot(userDocRef, (userDocSnap) => {
          if (userDocSnap.exists()) {
            const newUserData = userDocSnap.data();
            setUserData(newUserData);
            if (newUserData.teamId) {
              const teamDocRef = doc(db, 'teams', newUserData.teamId);
              unsubscribeTeamDoc = onSnapshot(teamDocRef, (teamDocSnap) => {
                if (teamDocSnap.exists()) {
                  const teamData = teamDocSnap.data();
                  const role = teamData.membri?.[currentUser.uid];
                  setUserRole(role || null);

                  setTeamLogos({
                    light: teamData.logoUrlLight || null,
                    dark: teamData.logoUrlDark || null,
                  });

                  if (teamData.trialEndsAt) {
                    setTrialEndsAt(teamData.trialEndsAt);
                    const isExpired = teamData.trialEndsAt.toDate() < new Date();
                    setIsTrialExpired(isExpired);
                  } else {
                    // trialEndsAt null = accesso permanente (piano attivo/pagante)
                    setTrialEndsAt(null);
                    setIsTrialExpired(false);
                  }
                } else {
                  setUserRole(null);
                  setIsTrialExpired(true);
                  setTeamLogos({ light: null, dark: null }); // Reset loghi
                }
                setIsLoading(false);
              });
            } else {
              setUserRole(null);
              setIsTrialExpired(true);
              setIsLoading(false);
              setTeamLogos({ light: null, dark: null }); // Reset loghi
            }
          } else {
            setUserData(null);
            setUserRole(null);
            setIsTrialExpired(true);
            setIsLoading(false);
            setTeamLogos({ light: null, dark: null }); // Reset loghi
          }
        });
      } else {
        setUser(null); setUserData(null); setUserRole(null);
        if (unsubscribeUserDoc) unsubscribeUserDoc();
        if (unsubscribeTeamDoc) unsubscribeTeamDoc();
        setIsLoading(false);
        setIsTrialExpired(true);
        setTeamLogos({ light: null, dark: null }); // Reset loghi
      }
    });
    return () => {
      unsubscribeAuth();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
      if (unsubscribeTeamDoc) unsubscribeTeamDoc();
    };
  }, []);

  if (isLoading) { return <div>Caricamento...</div>; }

  // --- Logica di routing con ThemeProvider e MainLayout ---
  const isTrial = !!trialEndsAt;

  return (
    <TeamPlanContext.Provider value={{ isTrial }}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        {/* EulaModal: Dialog floating sopra tutto, visibile solo se utente loggato */}
        {user && userData && <EulaModal user={user} userData={userData} />}
        <Routes>
          {/* STATO SUPER ADMIN: Dashboard Nascosta */}
          {user && userData && (
            <Route path="/super-admin" element={<SuperAdminDashboard user={user} userData={userData} />} />
          )}

          {/* STATO 1: Utente loggato (usa MainLayout) */}
          {user && userData && userData.teamId && userRole && (
            <Route path="/" element={
              <MainLayout
                userRole={userRole}
                mode={mode}
                toggleTheme={toggleTheme}
                isTrialExpired={isTrialExpired}
                trialEndsAt={trialEndsAt}
                teamLogos={teamLogos}
              />
            }>
              {/* --- MODIFICA: Passa i props a DashboardHome --- */}
              <Route
                index
                element={
                  userRole === 'Driver' ? (
                    <Navigate to="/driver-portal" replace />
                  ) : (
                    <DashboardHome
                      teamId={userData.teamId}
                      userRole={userRole}
                      userId={user.uid}
                      mode={mode}
                      teamLogos={teamLogos}
                    />
                  )
                }
              />
              {/* --- FINE MODIFICA --- */}
              <Route path="driver-portal" element={<DriverDashboard teamId={userData.teamId} userRole={userRole} userId={user.uid} mode={mode} teamLogos={teamLogos} />} />
              <Route path="garage" element={<Garage teamId={userData.teamId} userRole={userRole} userId={user.uid} />} />
              <Route path="vehicle/:vehicleId" element={<VehicleDashboard teamId={userData.teamId} userRole={userRole} />} />
              <Route path="vehicle/:vehicleId/setup/:setupId" element={<SetupDetail teamId={userData.teamId} userRole={userRole} />} />
              <Route path="vehicle/:vehicleId/setup/new" element={<SetupForm teamId={userData.teamId} userRole={userRole} />} />
              <Route path="vehicle/:vehicleId/setup/:setupId/edit" element={<SetupForm teamId={userData.teamId} userRole={userRole} />} />
              <Route path="setup-templates" element={<SetupTemplateManager teamId={userData.teamId} userRole={userRole} />} />
              <Route path="compare/:setupId1/with/:setupId2" element={<ComparePage teamId={userData.teamId} userRole={userRole} />} />
              <Route path="team-management" element={<TeamManagement teamId={userData.teamId} user={user} userRole={userRole} />} />
              <Route path="track-management" element={<TrackManagement teamId={userData.teamId} userRole={userRole} />} />
              <Route path="inventory" element={<Inventory teamId={userData.teamId} userRole={userRole} />} />
              <Route path="checklist-manager" element={<ChecklistManager teamId={userData.teamId} userRole={userRole} />} />
              <Route path="events-calendar" element={<EventsCalendar teamId={userData.teamId} userRole={userRole} />} />
              <Route path="event/:eventId" element={<EventDetail teamId={userData.teamId} userRole={userRole} />} />
              <Route path="category-manager" element={<CategoryManagement teamId={userData.teamId} userRole={userRole} />} />
              <Route path="supplier-manager" element={<SupplierManagement teamId={userData.teamId} userRole={userRole} />} />
              <Route path="order-manager" element={<OrderManagement teamId={userData.teamId} userRole={userRole} />} />
              <Route path="order/:orderId" element={<OrderDetail teamId={userData.teamId} userRole={userRole} />} />
              <Route path="driver-management" element={<DriverManagement teamId={userData.teamId} userRole={userRole} />} />
              <Route path="workshop" element={<WorkshopDashboard teamId={userData.teamId} userRole={userRole} />} />
              <Route path="workshop/new" element={<WorkOrderForm teamId={userData.teamId} userRole={userRole} />} />
              <Route path="workshop/:workOrderId" element={<WorkOrderDetail teamId={userData.teamId} userRole={userRole} userId={user.uid} />} />
              <Route path="workshop/:workOrderId/edit" element={<WorkOrderForm teamId={userData.teamId} userRole={userRole} />} />
              <Route path="billing" element={<BillingDashboard teamId={userData.teamId} userRole={userRole} />} />
              <Route path="billing/new" element={<BillingForm teamId={userData.teamId} userRole={userRole} userId={user.uid} />} />
              <Route path="billing/:summaryId" element={<BillingDetail teamId={userData.teamId} userRole={userRole} />} />
              <Route path="billing/:summaryId/edit" element={<BillingForm teamId={userData.teamId} userRole={userRole} userId={user.uid} />} />
              <Route path="accept-invite/:inviteId" element={<AcceptInvite user={user} />} />
              <Route path="race-engineer" element={<RaceEngineerPage teamId={userData.teamId} />} />
              <Route path="tires" element={<TireManagement teamId={userData.teamId} userRole={userRole} userId={user.uid} />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Route>

          )}

          {/* STATO 2: Utente loggato MA senza team (NO MainLayout) */}
          {user && (!userData || !userData.teamId || !userRole) && (
            <>
              {!userData && <Route path="*" element={<div>Caricamento dati utente...</div>} />}
              {userData && !userData.teamId && (<Route path="/join-team" element={<JoinTeam user={user} />} />)}
              <Route path="/accept-invite/:inviteId" element={<AcceptInvite user={user} />} />
              <Route path="*" element={<Navigate to={userData && !userData.teamId ? "/join-team" : "/accept-invite/pending"} />} />
            </>
          )}

          {/* STATO 3: Utente NON loggato (NO MainLayout) */}
          {!user && (
            <>
              <Route path="/auth" element={<Auth />} />
              <Route path="/accept-invite/:inviteId" element={<AcceptInvite user={null} />} />
              <Route path="*" element={<Navigate to="/auth" />} />
            </>
          )}

        </Routes>
      </BrowserRouter>
    </ThemeProvider>
    </TeamPlanContext.Provider>
  );
}

export default App;